import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { KeyRound, Mail, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { forgotPassword } = useAuth();
  const { theme } = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    setIsLoading(true);
    try {
      await forgotPassword(email);
      setSubmitted(true);
      toast.success("If that email is registered, a reset link has been sent.");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background bg-texture flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <img
            src={theme === "light" ? "/logos/Evntkit_Logo.png" : "/logos/Evntkit_Logo_White.png"}
            alt="evntkit"
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold uppercase tracking-tight">Forgot Password</h1>
          <p className="text-muted-foreground">We'll send you a reset link</p>
        </div>

        <Card className="tactical-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-cyan-400" />
              Reset Password
            </CardTitle>
            <CardDescription>
              Enter the email associated with your account and we'll send you a link to reset your password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="space-y-4 text-center py-4" data-testid="forgot-password-success">
                <CheckCircle2 className="h-12 w-12 text-green-400 mx-auto" />
                <div>
                  <p className="font-semibold">Check your inbox</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    If an account exists for <span className="text-foreground">{email}</span>, you'll receive a password reset link shortly. The link expires in 1 hour.
                  </p>
                </div>
                <Button asChild variant="outline" className="w-full" data-testid="forgot-password-back-to-login">
                  <Link to="/login">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Sign In
                  </Link>
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="label-tactical">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="tactical-input pl-10"
                      data-testid="forgot-password-email"
                      disabled={isLoading}
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-cyan-600 hover:bg-cyan-700"
                  disabled={isLoading}
                  data-testid="forgot-password-submit"
                >
                  {isLoading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
                  ) : (
                    <><KeyRound className="h-4 w-4 mr-2" />Send Reset Link</>
                  )}
                </Button>
              </form>
            )}

            {!submitted && (
              <div className="mt-6 text-center text-sm">
                <Link to="/login" className="text-cyan-400 hover:underline inline-flex items-center gap-1" data-testid="forgot-password-back-link">
                  <ArrowLeft className="h-3 w-3" />
                  Back to Sign In
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
